/**
 * Cookie helpers — read JS-readable cookies set by the auth gateway.
 *
 * The auth gateway (ADR-003 D2) sets two cookies on POST /api/auth/login:
 *   - SMARTDASH_SESSION (HttpOnly, Secure, SameSite=Lax) — opaque session id;
 *     JS cannot read it (browser-enforced). Sent automatically by the browser
 *     on every same-origin request.
 *   - SMARTDASH_CSRF (Secure, SameSite=Lax, JS-readable) — double-submit CSRF
 *     token; JS reads it and echoes it as the X-SmartDash-CSRF header on
 *     state-changing requests (POST /api/auth/logout, per OpenAPI).
 *
 * Reading cookies safely: this file only reads; it never writes or modifies
 * the document.cookie string. The gateway is the only writer (via Set-Cookie).
 */

const CSRF_COOKIE_NAME = 'SMARTDASH_CSRF'

/**
 * Return the named CSRF cookie (customer gateway by default), or null if the browser has
 * not received it yet (first load, logged out, etc.). Whitespace-trimmed; an
 * empty value is treated as absent.
 *
 * Uses document.cookie — only works for non-HttpOnly cookies. The session
 * cookie (HttpOnly) is intentionally unreadable from JS; the SPA never sees
 * its value, never writes it, never sends it in a header manually.
 */
export function readCsrfCookie(cookieName = CSRF_COOKIE_NAME): string | null {
  if (typeof document === 'undefined') return null
  const raw = document.cookie
  if (raw === '' || raw.length === 0) return null
  const target = `${cookieName}=`
  for (const segment of raw.split(';')) {
    const trimmed = segment.trim()
    if (trimmed.startsWith(target)) {
      const value = trimmed.slice(target.length).trim()
      return value.length > 0 ? value : null
    }
  }
  return null
}

/** Test-only: name export so unit tests can assert against the cookie name. */
export const SMARTDASH_CSRF_COOKIE_NAME = CSRF_COOKIE_NAME
